import { RWSService } from "@rws-framework/client";
import StorageService, { type StorageServiceInstance } from "src/services/StorageService";
import { getMockData } from "./mockData/courseDashboardServiceMock";
import { HTTPClient } from "./httpClient";
import { Cache } from "../helpers/cache";
import Cookies from "js-cookie";
import type { Course } from "../types/course";
import type { Activity } from "../types/activities";
import type { StudentEnrollment } from "../types/students";
import type { StatsAverage, Engagement, Loggedin } from "../types/statsAverage";
import type { RosterRecord } from "../types/users";
import type { Lesson } from "../types/collection";
import type { Quiz } from "../types/assessments";

const handleError = (err: unknown): never => {
  console.error(err);
  throw new Error('Failed to fetch the data');
};

export type CourseDashboardResourceType =
  | "course"
  | "students"
  | "engagement"
  | "average-score"
  | "summary-logged-in"
  | "summary-alerts"
  | "quiz-schedule"
  | "activities"
  | "activity_manager"
  | "most-recently-closed"
  | "enrollments"
  | "ltiStuff";

export class CourseDashboardService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private client = new HTTPClient({});
  private cache = new Cache();
  lesson1: Lesson = undefined;
  lesson2: Lesson = undefined;
  isCourseMock: Record<string, boolean> = {};

  constructor(@StorageService private storageService: StorageServiceInstance) {
    super();
  }

  // $stateParams.courseId
  get courseId(): string {
    return window.stateParams.courseId ?? null;
  }
  
  public flush(obj_type: string): void {
    const key = obj_type === "activity_manager" ? "activities" : obj_type;
    this.cache.clear(`course-dashboard-${key}-${this.courseId}`);
    this.client.clearCache(`course-dashboard-${key}-${this.courseId}`);
  }

  public async get(type: CourseDashboardResourceType, options = null) {
    const mock = this.isCourseMock[this.courseId];
  
    const resolveMock = async (mockValue: boolean) => {
      // store mock value
      this.isCourseMock[this.courseId] = !!mockValue;
      // adjust type if mock applies
      const finalType = mockValue && type !== "course"
        ? `${type}-mock`
        : type;
  
      return this._get(finalType, options);
    };
  
    // If mock value is already known → use it
    if (mock !== undefined) {
      return resolveMock(mock);
    }
  
    // Otherwise fetch from storageService
    const course: { mock: boolean } = await new Promise((resolve) => {
      this.storageService.get(
        "course",
        { courseId: this.courseId },
        (result) => resolve(result)
      );
    });
  
    return resolveMock(course.mock);
  }

  private getCourseUncached(courseId: string): Promise<Course> {
    return new Promise<Course>((resolve, reject) => {
      this.storageService.get("course", {courseId: courseId}, (course) => 
          this.addLessonData(course)
            .then(() => resolve(course))
            .catch((err) => reject(err))
      )
    });
  };

  private async addLessonData(course) {
    const res = await this.get("most-recently-closed");
    course.lessonsData.forEach((lesson: Lesson, i: number) => {
        lesson.place = i;
    });
    if (res.lesson) {
      this.lesson1 = course.lesson1 = course.lessonsData[res.lesson];
      this.lesson2 = course.lesson2 = course.lessonsData[Math.max(0, res.lesson - 1)];
    } else if (course.lessonsData.length) {
      this.lesson1 = course.lesson1 = course.lessonsData[Math.min(1, course.lessonsData.length - 1)];
      this.lesson2 = course.lesson2 = course.lessonsData[0];
    } else {
      this.lesson1 = course.lesson1 = {};
      this.lesson2 = course.lesson2 = {};
    }
  }

  private getStudentsUncached(courseId: string): Promise<RosterRecord[]> {
    return new Promise<RosterRecord[]>((resolve, reject) => {
      this.storageService.get("course", {courseId: courseId}, async (course) => {
      try {

        // stats:roster
        const res = await this.client.post(`/stats/roster/${courseId}/${course.slug}`,
          {
            body: {
              ignore: course.instructors.concat(course.assistants),
            },
          }
        )

          resolve(res);
        } catch (err) {
          reject(err);
        }
      });
    });
  }

  private async getEngagementUncached(): Promise<Engagement> {
    const [summaryLoggedIn, students, avgScore] = await Promise.all([
      this.get("summary-logged-in"),
      this.get("students"),
      this.get("average-score"),
    ]).catch(handleError);

    const rawEngagement = {
      ...summaryLoggedIn.engagement,
      engaged: avgScore.engagement.engaged,
    };
    const engagement = {
      disengaged: [],
      struggling: [],
      engaged: [],
      percent: undefined,
      insight: undefined,
      insight_list: [],
    };

    const ids = students.map((s: RosterRecord) => s._id);
    for (const student of students) {
      const id = student._id;
      if (rawEngagement.engaged.includes(id)) {
        engagement.engaged.push(student);
      } else if (rawEngagement.active.includes(id)) {
        engagement.struggling.push(student);
      } else if (!rawEngagement.disengaged.includes(id)) {
        engagement.disengaged.push(student);
      }
    }

    for (const id of rawEngagement.disengaged) {
      const idx = ids.indexOf(id);
      if (idx !== -1 && !rawEngagement.engaged.includes(id)) {
        engagement.disengaged.push(students[idx]);
      }
    }

    engagement.percent = Math.round(
      (100 * engagement.engaged.length) / Math.max(1, students.length)
    );

    const avgInsight = avgScore.insight ?? undefined;
    const summaryInsight = summaryLoggedIn.insight ?? undefined;

    if (avgInsight && !(summaryLoggedIn.insight && avgInsight[1]?.includes("Looks like"))) {
      engagement.insight = avgInsight[1];
      engagement.insight_list = avgInsight[0];
    } else if (summaryInsight) {
      engagement.insight = summaryInsight[1];
      engagement.insight_list = summaryInsight[0];
    }

    return engagement;
  }

  private getAverage({ activity_id, skipCache }: { activity_id?: string, skipCache?: boolean } = {}): Promise<StatsAverage> {
    // stats:average
    return this.client.get(`/stats/average/${this.courseId}`, {
      search: { allow_rollup: 'false', ...(activity_id && { activity_id }) },
      ...(!skipCache && { cache: `course-dashboard-average-${this.courseId}${activity_id ? "-" + activity_id : ""}` }),
    }).catch(handleError);
  }

  private getGradebookAverage(): Promise<StatsAverage> {
    // stats:average
    return this.client.get(`/stats/average/${this.courseId}`, {
      search: { allow_rollup: 'true' },
      cache: `course-dashboard-gradebookAverage-${this.courseId}`,
    }).catch(handleError);
  }

  private async getSummaryAlerts(): Promise<Record<string, any>> {
    const res = await this.get("course")
      // stats:alerts
      .then((course) => this.client.get(`/stats/alerts/${this.courseId}/${course.open_date}/${this.lesson1.place}`, {
        cache: `course-dashboard-summary-alerts-${this.courseId}`,
      }))
      .catch(handleError);
    return res.alerts.sort((a, b) => a.time > b.time).slice(0, 2).reverse();
  }

  private getActivities(): Promise<Activity[]> {
    // stats:activities
    return this.client.get(`/stats/activities/${this.courseId}`, {
      cache: `course-dashboard-activities-${this.courseId}`,
    }).catch(handleError);
  }

  private getActivityManagerUncached(): Promise<Activity[]> {
    return this.get("activities").catch(handleError);
  }

  private async getRecentlyClosedUncached(): Promise<Record<string, number>> {
    const activities = await this.get("activity_manager");
    return activities.length ? { lesson: activities[0].lesson1 } : {};
  }

  private getLtiStuff(): Promise<Record<string, any>> {
    // LTI:course:stuff
    return this.client.get(`/ltistuff/${this.courseId}`).catch(handleError);
  }

  private async getQuizUncached(): Promise<Record<'calendar_data', Quiz[]>> {
    const [course, activities] = await Promise.all([
      this.get("course"),
      this.get("activity_manager"),
    ]).catch(handleError);

    const calendarItems: Quiz[] = [];

    for (const lesson of course.lessonsData) {
      const e = course.course_data[lesson._id] || {};
      const startDate = e.open_date ? new Date(e.open_date) : undefined;
      lesson.suppressed =
        e.suppress_until_available &&
        startDate &&
        startDate.getTime() > Date.now();

      if (startDate && !lesson.suppressed) {
        calendarItems.push({
          date: startDate,
          status: "open",
          index: 0,
          lessonName: lesson.title,
          quizName: "Start Date",
          link: `${this.courseId}/${lesson._id}`,
        });
      }
    }

    activities.forEach((activity: Activity, i: number) => {
      if (!activity.lesson) return;

      let link = `${activity.link}/${activity.id}`;
      if (activity.type === "comments") {
        link += "?view=shared";
      }

      // TODO filter on scheduled lessons and pages (lesson start dates
      // are dealt with above but we should not show anything inside lessons and pages not started yet)
      if (activity.close_date && activity.close_date < "2198" || activity.due_date) {
        const closeDate = new Date(activity.close_date || "2199-02-01T00:00:01Z");
        const dueDate = new Date(activity.due_date || "2199-02-01T00:00:01Z");

        if (dueDate < new Date("2198")) {
          calendarItems.push({
            date: dueDate,
            status: "scheduled",
            lessonName: activity.lessonName,
            quizName: activity.report_title,
            link,
            index: i,
          });
        }

        if (new Date() < closeDate && closeDate < new Date("2198")) {
          calendarItems.push({
            date: closeDate,
            status: "scheduled",
            lessonName: activity.lessonName,
            quizName: activity.report_title,
            link,
            index: i,
          });
        } else if (closeDate < new Date()) {
          calendarItems.push({
            date: closeDate,
            status: "closed",
            lessonName: activity.lessonName,
            quizName: activity.report_title,
            link,
            index: i,
          });
        }
      } else {
        // these are the items with no date (show at the end regardless of chosen month)
        // but only if they are graded with a max score
        if (activity.max_score && activity.graded && !activity.suppress_until_available) {
          calendarItems.push({
            lessonName: activity.lessonName,
            quizName: activity.report_title,
            link,
            index: i,
          });
        }
      }

      if (activity.open_date && activity.type !== "lesson" && !activity.suppress_until_available) {
        const openDate = new Date(activity.open_date);
        calendarItems.push({
          date: openDate,
          status: "open",
          lessonName: activity.lessonName,
          quizName: activity.report_title,
          link,
          index: i,
        });
      }
    });

    return { calendar_data: calendarItems };
  }

  private getEnrollments(): Promise<StudentEnrollment[]> {
    // stats:enrollments
    return this.client.get(`/stats/enrollments/${this.courseId}`, {
      cache: `course-dashboard-enrollments-${this.courseId}`,
    }).catch(handleError);
  }

  private async getSummaryLoggedIn(): Promise<Loggedin> {
    const unit = window.labelBundle.lesson_unit || "L";
    const _course = await this.get("course");
    const place = this.lesson1.place;

    const [students, loggedin] = await Promise.all([
      this.get("students"),
      // stats:loggedin
      this.client.get(`/stats/loggedin/${this.courseId}/${place}`),
    ]).catch(handleError);

    const plabel = unit + Math.max(1, place);
    const label = unit + (place + 1);

    loggedin.students = students;
    loggedin.trend = loggedin.trenddict[label];
    loggedin.trendmins = loggedin.trendmindict[label];
    loggedin.count = loggedin.student_times[label]?.length ?? 0;
    loggedin.prior = loggedin.student_times[plabel]?.length ?? 0;
    loggedin.mins = loggedin.lesson_times[label] ?? 0;
    loggedin.priormins = loggedin.lesson_times[plabel] ?? 0;

    return loggedin;
  }
  
  private async _get(type: string, options = null): Promise<any> {
    switch (type) {
      case "course": {
        const course = await this.getCourse();
        this.addLessonData(course).catch(console.error)
        return course;
      }
      
      case "course-mock": {
        return this.get("course");
      }
  
      case "students": {
        return this.getStudents();
      }

      case "students-mock": {
        return getMockData("students-mock");
      }

      /* ENGAGEMENT */
      case "engagement": {
        return this.getEngagement();
      }
  
      case "engagement-mock": {
        return getMockData("engagement-mock");
      }
  
      /* GET AVERAGE SCORE */
      case "average-score": {
        const cacheKey = options && options.gradebook && Cookies.get("viewL") === "custom" ? "gradebookAverage" : "average";
        if (options?.invalidateCache) {
          this.client.clearCache(`course-dashboard-${cacheKey}-${this.courseId}`);
        }
        if (options?.activity_id) {
          return this.getAverage({ activity_id: options.activity_id, skipCache: true });
        }
        return cacheKey === 'average'
          ? this.getAverage()
          : this.getGradebookAverage();
      }
          
      case "average-score-mock": {
        return getMockData("average-score-mock");
      }

      /* GET LOGGED IN SUMMARY */
      case "summary-logged-in": {
        return this.getSummaryLoggedIn();
      }

      case "summary-logged-in-mock": {
        return getMockData("summary-logged-in-mock");
      }

      /* GET ALERTS SUMMARY */
      case "summary-alerts": {
        return this.getSummaryAlerts();
      }

      case "summary-alerts-mock": {
        return getMockData("summary-alerts-mock");
      }

      /* GET QUIZ SCHEDULE */
      case "quiz-schedule": {
        if (options && options.invalidateCache) {
          this.cache.clear(`course-dashboard-quiz-${this.courseId}`);
        }
        return this.getQuiz();
      }

      case "quiz-schedule-mock": {
        return getMockData("quiz-schedule-mock");
      }

      /* GET ACTIVITIES */
      case "activities": {
        if (options?.invalidateCache) {
          this.client.clearCache(`course-dashboard-activities-${this.courseId}`)
        }
        return this.getActivities();
      }
          
      case "activities-mock": {
        return getMockData("activities-mock");
      }
                  
      /* GET ACTIVITY MANAGER */
      case "activity_manager": {
        return this.getActivities();
      }

      case "most-recently-closed": {
        return this.getRecentlyClosed()
      }

      case "most-recently-closed-mock": {
        return getMockData("most-recently-closed-mock");
      }
                  
      /* GET ENROLLMENTS */
      case "enrollments": {
        return this.getEnrollments();
      }
      
      case "ltiStuff": {
        return this.getLtiStuff();
      }

      default: {
        return Promise.reject("Valid type of GET is required");
      }      
    }
  }

  getCourse = this.cache.cached(
    () => `course-dashboard-course-${this.courseId}`,
    () => this.getCourseUncached(this.courseId),
  )

  getStudents = this.cache.cached(
    () => `course-dashboard-students-${this.courseId}`,
    () => this.getStudentsUncached(this.courseId),
  )

  getEngagement = this.cache.cached(
    () => `course-dashboard-engagement-${this.courseId}`,
    () => this.getEngagementUncached(),
  )

  getActivityManager = this.cache.cached(
    () => `course-dashboard-activity-manager-${this.courseId}`,
    () => this.getActivityManagerUncached(),
  )

  getRecentlyClosed = this.cache.cached(
    () => `course-dashboard-most-recently-closed-${this.courseId}`,
    () => this.getRecentlyClosedUncached(),
  )

  getQuiz = this.cache.cached(
    () => `course-dashboard-quiz-${this.courseId}`,
    () => this.getQuizUncached(),
  )
}

export default CourseDashboardService.getSingleton()

export {CourseDashboardService as CourseDashboardServiceInstance}
